import { HTMLAttributes, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';
import { Button } from '@revenuemarket/cds';
import { Partner } from '@revenuemarket/utils/partners/getPartnerByHostname';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import ContentWriterImage from '~assets/images/finance/contentwriter.svg';
import DataEncryptionImage from '~assets/images/finance/dataencryption.svg';
import FinancialPlanImage from '~assets/images/finance/financialplan.svg';
import ScienceImage from '~assets/images/finance/science.svg';
import useIntroAutoCapture from '~hooks/useIntroAutoCapture';

const APP_URL = 'app.clobe.ai';

const IntroPage = ({ title, value }: { title: string; value: Partner }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCaptureMode, setIsCaptureMode] = useState(false);
  const isWtoptax = value === 'wtoptax';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    setIsCaptureMode(params.get('download') === '1');
  }, []);

  useIntroAutoCapture({
    containerRef,
    getFileName: () => `${title} 고객안내문`,
  });

  return (
    <>
      <Head>
        <title>{title} 고객 전용 자료 전송 간편화 안내</title>
        <meta
          name="description"
          content={`${title} 고객 전용으로 자료 전송을 간편화 할 수 있는 서비스입니다.`}
        />
      </Head>

      <Container ref={containerRef} capture={isCaptureMode}>
        <HeaderSection>
          <TitleContainer>
            <TitleText>{title} 고객 전용</TitleText>
            <TitleText>자료 전송 간편화 안내</TitleText>
          </TitleContainer>
          <DescriptionText>
            <span>최초 1회 공동인증서 연동을 통해 자료 전송이 간편화 됩니다.</span>
            <span>앞으로는 결산·신고 자료를 개별적으로 요청드리지 않을 예정입니다.</span>
          </DescriptionText>
        </HeaderSection>
        <ContentWrapper>
          {/* Header Section */}

          {/* Process Steps Section */}
          <ProcessSection>
            {/* Step 1 */}

            <StepContent bgColor="#2A82FF">
              <IconContainer>
                <Image src={ContentWriterImage} alt="자료 전송 자동화" width={138} height={138} />
              </IconContainer>
              <StepInfoContainer>
                <StepHeader>
                  <StepNumber color="#006AFF" data-capture-step-number>
                    1
                  </StepNumber>
                  <StepTitle>자료 전송 자동화</StepTitle>
                </StepHeader>
                <StepDescription>
                  <BulletPoint>
                    <StepDescriptionText>
                      통장내역, 카드내역, 4대보험, 수출입내역, PG/마켓플레이스 등 모든 자료 자동
                      전송
                    </StepDescriptionText>
                  </BulletPoint>
                </StepDescription>
              </StepInfoContainer>
            </StepContent>

            {/* Step 2 */}

            <StepContent bgColor="#A03AFF">
              <IconContainer>
                <Image src={FinancialPlanImage} alt="실시간 자금관리" width={138} height={138} />
              </IconContainer>
              <StepInfoContainer>
                <StepHeader>
                  <StepNumber color="#8400FF" data-capture-step-number>
                    2
                  </StepNumber>
                  <StepTitle>실시간 자금관리 기능</StepTitle>
                </StepHeader>
                <StepDescription>
                  <BulletPoint>
                    <StepDescriptionText>
                      현금흐름, 자금일보, 매출·매입 정산,
                      <br /> 입출금 및 계산서 발행 알림 기능 제공
                    </StepDescriptionText>
                  </BulletPoint>
                </StepDescription>
              </StepInfoContainer>
            </StepContent>

            {/* Step 3 */}

            <StepContent bgColor="#2ABBF0">
              <IconContainer>
                <Image src={DataEncryptionImage} alt="간편한 자금관리" width={138} height={138} />
              </IconContainer>
              <StepInfoContainer>
                <StepHeader>
                  <StepNumber color="#00ACEA" data-capture-step-number>
                    3
                  </StepNumber>
                  <StepTitle>간편한 자금관리 업무</StepTitle>
                </StepHeader>
                <StepDescription>
                  <BulletPoint>
                    <StepDescriptionText>
                      세금계산서 발행, 하나은행 이체 등 <br />
                      자금관리 업무 기능 제공
                    </StepDescriptionText>
                  </BulletPoint>
                </StepDescription>
              </StepInfoContainer>
            </StepContent>

            {/* Step 4 */}

            <StepContent bgColor="#5D5DFF">
              <IconContainer>
                <Image src={ScienceImage} alt="요금 안내" width={138} height={138} />
              </IconContainer>
              <StepInfoContainer>
                <StepHeader>
                  <StepNumber color="#4545FF" data-capture-step-number>
                    4
                  </StepNumber>
                  <StepTitle>{isWtoptax ? '기타' : '요금 안내'}</StepTitle>
                </StepHeader>
                <StepDescription>
                  {isWtoptax ? (
                    <div>
                      <BulletPoint>
                        <StepDescriptionText>고객 전용 요금 무료</StepDescriptionText>
                      </BulletPoint>
                      <BulletPoint>
                        <StepDescriptionText>제1금융권 보안 기준 충족</StepDescriptionText>
                      </BulletPoint>
                    </div>
                  ) : (
                    <BulletPoint>
                      <StepDescriptionText>평생 무료로 제공</StepDescriptionText>
                    </BulletPoint>
                  )}
                </StepDescription>
              </StepInfoContainer>
            </StepContent>
          </ProcessSection>
        </ContentWrapper>
      </Container>
    </>
  );
};

// 레이아웃을 완전히 제거하여 독립적인 페이지로 만듦
IntroPage.getLayout = function getLayout(page: ReactElement) {
  return page;
};

// Styled Components
const Container = styled.div<{ capture?: boolean }>`
  width: ${(props) => (props.capture ? '1190px' : '100%')};
  height: ${(props) => (props.capture ? '1440px' : 'auto')};
  min-height: ${(props) => (props.capture ? '1440px' : '100vh')};
  background: linear-gradient(180deg, #fcfcfe 0%, #dfe7f1 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: ${(props) => (props.capture ? '114px 60px 0px' : '227px 40px 200px 40px')};

  @media (max-width: 1040px) {
    padding: 227px 40px 40px;
  }

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1114px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;

  @media (max-width: 1040px) {
    max-width: 650px;
  }

  @media (max-width: 768px) {
    max-width: 375px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 714px;
  margin-bottom: 104px;

  @media (max-width: 1040px) {
    width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 54px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 800px;

  @media (max-width: 1040px) {
    width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TitleText = styled.div`
  color: #006aff;
  text-align: center;
  font-family: 'Pretendard';
  font-size: 60px;
  font-weight: 800;
  line-height: 120%;
  letter-spacing: -1.2px;

  @media (max-width: 1040px) {
    font-size: 48px;
    line-height: 140%;
  }

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 130%;
  }
`;

const DescriptionText = styled.div`
  width: 649px;
  font-family: 'Pretendard';
  font-weight: 500;
  font-size: 24px;
  line-height: 140%;
  text-align: center;
  letter-spacing: -0.02em;
  color: #505155;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 1040px) {
    width: 100%;
    font-size: 20px;
    line-height: 160%;
  }

  @media (max-width: 768px) {
    width: 100%;
    font-size: 16px;
    line-height: 150%;
  }
`;

const ProcessSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 1114px;
  flex-wrap: wrap;

  @media (max-width: 1040px) {
    width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StepContent = styled.div<{ bgColor: string }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 42px 48px 50px 44px;
  gap: 20px;
  width: 100%;
  background: ${(props) => props.bgColor};
  border-radius: 56px;
  width: 552px;
  height: 346px;

  @media (max-width: 1040px) {
    padding: 32px 38px 40px 34px;
    gap: 20px;
    width: calc(50% - 4px);
  }

  @media (max-width: 768px) {
    padding: 32px 38px 40px 34px;
    gap: 20px;
    width: 100%;
    height: 300px;
  }
`;

const StepInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 30px;
  flex: 1;

  @media (max-width: 1040px) {
    gap: 24px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StepHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 14px;
`;

// const StepNumber = styled.div<{ color: string }>`
//   display: flex;
//   flex-direction: column;
//   justify-content: center;
//   align-items: center;
//   padding: 6px;
//   width: 32px;
//   height: 32px;
//   background: #ffffff;
//   border-radius: 80px;

//   font-family: 'Pretendard';
//   font-weight: 800;
//   font-size: 20px;
//   line-height: 25px;
//   text-align: center;
//   letter-spacing: -0.02em;
//   color: ${(props) => props.color};
// `;
const StepNumber = ({
  color,
  children,
  ...rest
}: { color: string; children: ReactNode } & HTMLAttributes<HTMLDivElement>) => {
  const label =
    typeof children === 'string' || typeof children === 'number' ? String(children).trim() : '';
  const svg = `<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>\n  <circle cx='16' cy='16' r='16' fill='#FFFFFF' />\n  <text x='16' y='16' text-anchor='middle' dominant-baseline='central' dy='0.08em' font-family='Pretendard' font-weight='800' font-size='20' fill='${color}'>${label}</text>\n</svg>`;
  const dataUrl = `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
  return (
    <div
      {...rest}
      style={{
        width: 32,
        height: 32,
        backgroundImage: dataUrl,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%',
        display: 'block',
      }}
    />
  );
};

const StepTitle = styled.div`
  font-family: 'Pretendard';
  font-weight: 800;
  font-size: 32px;
  line-height: 100%;
  letter-spacing: -0.02em;
  color: #ffffff;
  white-space: nowrap;

  @media (max-width: 1040px) {
    font-size: 24px;
    line-height: 120%;
    white-space: normal;
  }

  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 130%;
  }
`;

const StepDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;

  @media (max-width: 1040px) {
    gap: 16px;
  }
`;

const BulletPoint = styled.div`
  position: relative;
  padding-left: 18px;
  width: 100%;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #ffffff;
    font-size: 26px;
    line-height: 100%;

    @media (max-width: 1040px) {
      font-size: 22px;
    }

    @media (max-width: 768px) {
      font-size: 18px;
    }
  }
`;

const StepDescriptionText = styled.div`
  font-family: 'Pretendard';
  font-weight: 500;
  font-size: 28px;
  line-height: 120%;
  letter-spacing: -0.56px;
  color: #ffffff;

  @media (max-width: 1040px) {
    font-size: 20px;
    line-height: 130%;
  }

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 140%;
  }
`;

const IconContainer = styled.div`
  width: 104px;
  height: 104px;
  flex-shrink: 0;
  align-self: flex-end;

  & > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 1040px) {
    width: 90px;
    height: 90px;
  }

  @media (max-width: 768px) {
    align-self: flex-start;
    margin: 0;
    width: 70px;
    height: 70px;
  }
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const CTAButton = styled(Button)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 19px 36px;
  gap: 10px;
  width: 223px;
  height: 68px;
  background: #006aff;
  box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.1);
  border-radius: 40px;
  border: none;

  font-family: 'Pretendard';
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  letter-spacing: -0.4px;
  color: #ffffff;

  &:hover {
    background: #4545ff;
  }
`;

const CTAIcon = () => {
  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        marginLeft: 10,
      }}
    >
      <path
        d="M1.5 12C1.5 18.1 6.4 23 12.5 23C18.6 23 23.5 18.1 23.5 12C23.5 5.9 18.6 1 12.5 1C6.4 1 1.5 5.9 1.5 12ZM7 12C7 11.4 7.4 11 8 11H14.6L12.3 8.7C11.9 8.3 11.9 7.7 12.3 7.3C12.7 6.9 13.3 6.9 13.7 7.3L17.7 11.3C18.1 11.7 18.1 12.3 17.7 12.7L13.7 16.7C13.5 16.9 13.3 17 13 17C12.7 17 12.5 16.9 12.3 16.7C11.9 16.3 11.9 15.7 12.3 15.3L14.6 13H8C7.5 13 7 12.6 7 12Z"
        fill="white"
      />
    </svg>
  );
};

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  width: 100%;
  max-width: 1054px;
  margin-top: 40px;

  @media (max-width: 768px) {
    align-items: center;
    margin-top: 20px;
  }
`;

const LogoContainer = styled.div<{ partner: Partner }>`
  width: ${(props) =>
    (() => {
      switch (props.partner) {
        case 'savetax':
          return '161px';
        case 'loum':
          return '332px';
        case 'thek':
          return '120px';
        case 'bdo':
          return '160px';
        case 'jiwoo':
          return '164px';
        default:
          return '200px';
      }
    })()};
  height: ${(props) =>
    (() => {
      switch (props.partner) {
        case 'savetax':
          return '23px';
        case 'loum':
          return '85px';
        case 'thek':
          return '35px';
        case 'bdo':
          return '62px';
        case 'jiwoo':
          return '30px';
        default:
          return '23px';
      }
    })()};
  position: relative;

  img {
    width: 100% !important;
    height: 100% !important;
    object-fit: contain;
  }
`;

// 인증 없이 접근 가능한 공개 페이지로 설정
export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const host = context.req.headers.host;

  if (host?.includes(APP_URL)) {
    return {
      redirect: {
        permanent: true,
        destination: 'https://app.clobe.ai',
      },
    };
  }

  return {
    props: {
      host,
    },
  };
};

export default IntroPage;
